import { useMemo, useState } from "react";

import GameLauncher from "../shared/GameLauncher";
import GameProgress from "../shared/GameProgress";
import GameResults from "../shared/GameResults";
import GameInterpretation from "../shared/GameInterpretation.jsx";

import { getBasicInterpretation } from "../shared/gameInterpretation";

import GameFields from "../shared/GameFields";
import resolveGameConfig from "../shared/resolveGameConfig";

import {
  calculateProgress,
  calculateSelectionAccuracy,
  calculateAverageReactionTime,
  calculateTotalTime,
  calculateErrorCount,
} from "../shared/gameMetrics";

import "./SentenceImageMatch.css";

const SCENES_BANK = [
  {
    id: "buckets",
    difficulty: "easy",
    category: "Colores y comparación",
    background: "laboratory",
    items: [
      {
        id: "bucket-blue",
        emoji: "🪣",
        label: "Cubeta azul",
        x: 28,
        y: 58,
        size: 74,
        badge: "AZUL",
        badgeColor: "#4e92df",
      },
      {
        id: "bucket-red",
        emoji: "🪣",
        label: "Cubeta roja",
        x: 70,
        y: 58,
        size: 74,
        badge: "ROJO",
        badgeColor: "#d95757",
      },
    ],
    statements: [
      {
        id: "buckets-same-shape",
        text: "Las dos cubetas tienen la misma forma.",
        isCorrect: true,
      },
      {
        id: "buckets-blue",
        text: "Las dos cubetas contienen líquido azul.",
        isCorrect: false,
      },
      {
        id: "buckets-red",
        text: "Una de las cubetas contiene líquido rojo.",
        isCorrect: true,
      },
      {
        id: "buckets-three",
        text: "En la imagen aparecen tres cubetas.",
        isCorrect: false,
      },
    ],
  },
  {
    id: "girl",
    difficulty: "easy",
    category: "Descripción de personas",
    background: "room",
    items: [
      {
        id: "girl-main",
        emoji: "👧🏼",
        label: "Niña rubia",
        x: 50,
        y: 52,
        size: 118,
      },
      {
        id: "glasses",
        emoji: "👓",
        label: "Gafas",
        x: 50,
        y: 35,
        size: 44,
      },
      {
        id: "shirt",
        emoji: "👕",
        label: "Camiseta azul",
        x: 50,
        y: 72,
        size: 62,
      },
    ],
    statements: [
      {
        id: "girl-glasses",
        text: "La niña lleva gafas.",
        isCorrect: true,
      },
      {
        id: "girl-shirt",
        text: "La niña lleva una camiseta azul.",
        isCorrect: true,
      },
      {
        id: "girl-sad",
        text: "La niña está llorando.",
        isCorrect: false,
      },
      {
        id: "girl-hat",
        text: "La niña lleva un sombrero rojo.",
        isCorrect: false,
      },
    ],
  },
  {
    id: "kite-house",
    difficulty: "easy",
    category: "Conceptos espaciales",
    background: "field",
    items: [
      {
        id: "house",
        emoji: "🏠",
        label: "Casa",
        x: 35,
        y: 68,
        size: 105,
      },
      {
        id: "kite",
        emoji: "🪁",
        label: "Cometa",
        x: 70,
        y: 25,
        size: 82,
      },
      {
        id: "sun",
        emoji: "☀️",
        label: "Sol",
        x: 18,
        y: 18,
        size: 58,
      },
    ],
    statements: [
      {
        id: "kite-above",
        text: "La cometa está por encima de la casa.",
        isCorrect: true,
      },
      {
        id: "kite-right",
        text: "La cometa está a la derecha de la casa.",
        isCorrect: true,
      },
      {
        id: "house-above",
        text: "La casa está por encima de la cometa.",
        isCorrect: false,
      },
      {
        id: "sun-below",
        text: "El sol está debajo de la casa.",
        isCorrect: false,
      },
    ],
  },
  {
    id: "soup-glass",
    difficulty: "easy",
    category: "Conceptos espaciales",
    background: "table",
    items: [
      {
        id: "soup",
        emoji: "🥣",
        label: "Sopa",
        x: 32,
        y: 58,
        size: 90,
      },
      {
        id: "glass",
        emoji: "🥛",
        label: "Vaso",
        x: 70,
        y: 55,
        size: 82,
      },
      {
        id: "spoon",
        emoji: "🥄",
        label: "Cuchara",
        x: 35,
        y: 34,
        size: 62,
      },
    ],
    statements: [
      {
        id: "glass-right",
        text: "El vaso está a la derecha de la sopa.",
        isCorrect: true,
      },
      {
        id: "soup-right",
        text: "La sopa está a la derecha del vaso.",
        isCorrect: false,
      },
      {
        id: "spoon-near-soup",
        text: "La cuchara está junto a la sopa.",
        isCorrect: true,
      },
      {
        id: "spoon-glass",
        text: "La cuchara está dentro del vaso.",
        isCorrect: false,
      },
    ],
  },
  {
    id: "shirts",
    difficulty: "easy",
    category: "Colores y cantidades",
    background: "closet",
    items: [
      {
        id: "shirt-yellow",
        emoji: "👕",
        label: "Camiseta amarilla",
        x: 23,
        y: 53,
        size: 88,
        badge: "AMARILLA",
        badgeColor: "#e7b93f",
      },
      {
        id: "shirt-blue-one",
        emoji: "👕",
        label: "Camiseta azul",
        x: 50,
        y: 53,
        size: 88,
        badge: "AZUL",
        badgeColor: "#4e92df",
      },
      {
        id: "shirt-blue-two",
        emoji: "👕",
        label: "Camiseta azul",
        x: 77,
        y: 53,
        size: 88,
        badge: "AZUL",
        badgeColor: "#4e92df",
      },
    ],
    statements: [
      {
        id: "shirts-three",
        text: "Hay tres camisetas.",
        isCorrect: true,
      },
      {
        id: "shirts-two-blue",
        text: "Hay dos camisetas azules.",
        isCorrect: true,
      },
      {
        id: "shirts-yellow-left",
        text: "La camiseta amarilla está a la izquierda.",
        isCorrect: true,
      },
      {
        id: "shirts-all-blue",
        text: "Todas las camisetas son azules.",
        isCorrect: false,
      },
    ],
  },
  {
    id: "car-tree",
    difficulty: "medium",
    category: "Relaciones espaciales",
    background: "field",
    items: [
      {
        id: "car",
        emoji: "🚙",
        label: "Coche azul",
        x: 28,
        y: 69,
        size: 90,
      },
      {
        id: "tree",
        emoji: "🌳",
        label: "Árbol",
        x: 70,
        y: 54,
        size: 125,
      },
      {
        id: "sun",
        emoji: "☀️",
        label: "Sol",
        x: 74,
        y: 16,
        size: 58,
      },
      {
        id: "cloud",
        emoji: "☁️",
        label: "Nube",
        x: 25,
        y: 22,
        size: 66,
      },
    ],
    statements: [
      {
        id: "car-left-tree",
        text: "El coche está a la izquierda del árbol.",
        isCorrect: true,
      },
      {
        id: "tree-right-car",
        text: "El árbol está a la derecha del coche.",
        isCorrect: true,
      },
      {
        id: "tree-below-sun",
        text: "El árbol está debajo del sol.",
        isCorrect: true,
      },
      {
        id: "car-above-cloud",
        text: "El coche está por encima de la nube.",
        isCorrect: false,
      },
      {
        id: "sun-left",
        text: "El sol está a la izquierda del coche.",
        isCorrect: false,
      },
    ],
  },
  {
    id: "transport",
    difficulty: "medium",
    category: "Categorías y posiciones",
    background: "transport",
    items: [
      {
        id: "ship",
        emoji: "🚢",
        label: "Barco",
        x: 20,
        y: 57,
        size: 94,
      },
      {
        id: "plane",
        emoji: "✈️",
        label: "Avión",
        x: 50,
        y: 31,
        size: 88,
      },
      {
        id: "bus",
        emoji: "🚌",
        label: "Autobús",
        x: 79,
        y: 61,
        size: 92,
      },
    ],
    statements: [
      {
        id: "transport-all",
        text: "Los tres objetos son medios de transporte.",
        isCorrect: true,
      },
      {
        id: "plane-middle",
        text: "El avión está entre el barco y el autobús.",
        isCorrect: true,
      },
      {
        id: "all-wheels",
        text: "Los tres medios de transporte tienen ruedas.",
        isCorrect: false,
      },
      {
        id: "all-sea",
        text: "Los tres pueden viajar por el mar.",
        isCorrect: false,
      },
      {
        id: "ship-left",
        text: "El barco está a la izquierda del avión.",
        isCorrect: true,
      },
    ],
  },
  {
    id: "bus-church",
    difficulty: "medium",
    category: "Posiciones y descripción",
    background: "town",
    items: [
      {
        id: "church",
        emoji: "⛪",
        label: "Iglesia",
        x: 66,
        y: 46,
        size: 140,
      },
      {
        id: "bus",
        emoji: "🚌",
        label: "Autobús amarillo",
        x: 31,
        y: 69,
        size: 100,
      },
      {
        id: "flag",
        emoji: "🚩",
        label: "Bandera roja",
        x: 70,
        y: 18,
        size: 48,
      },
    ],
    statements: [
      {
        id: "church-right",
        text: "La iglesia está a la derecha del autobús.",
        isCorrect: true,
      },
      {
        id: "bus-left",
        text: "El autobús está a la izquierda de la iglesia.",
        isCorrect: true,
      },
      {
        id: "flag-red",
        text: "La iglesia tiene una bandera roja.",
        isCorrect: true,
      },
      {
        id: "bus-red",
        text: "El autobús es de color rojo.",
        isCorrect: false,
      },
      {
        id: "church-left",
        text: "La iglesia está a la izquierda del autobús.",
        isCorrect: false,
      },
    ],
  },
  {
    id: "clowns",
    difficulty: "medium",
    category: "Atención a detalles",
    background: "circus",
    items: [
      {
        id: "clown-left",
        emoji: "🤡",
        label: "Payaso con nariz naranja",
        x: 31,
        y: 52,
        size: 118,
        badge: "NARANJA",
        badgeColor: "#e8943a",
      },
      {
        id: "clown-right",
        emoji: "🤡",
        label: "Payaso con nariz azul",
        x: 70,
        y: 52,
        size: 118,
        badge: "AZUL",
        badgeColor: "#4e92df",
      },
      {
        id: "star-left",
        emoji: "⭐",
        label: "Estrella amarilla",
        x: 26,
        y: 22,
        size: 42,
      },
    ],
    statements: [
      {
        id: "clown-blue-right",
        text: "El muñeco de la derecha tiene la nariz azul.",
        isCorrect: true,
      },
      {
        id: "clown-star-left",
        text: "El muñeco de la izquierda tiene una estrella amarilla.",
        isCorrect: true,
      },
      {
        id: "clowns-both-blue",
        text: "Los dos muñecos tienen la nariz azul.",
        isCorrect: false,
      },
      {
        id: "clown-blue-left",
        text: "El muñeco de la izquierda tiene la nariz azul.",
        isCorrect: false,
      },
      {
        id: "clowns-three",
        text: "En la imagen aparecen tres muñecos.",
        isCorrect: false,
      },
    ],
  },
  {
    id: "fruit-flipflops",
    difficulty: "medium",
    category: "Orden espacial",
    background: "beach",
    items: [
      {
        id: "watermelon",
        emoji: "🍉",
        label: "Sandía",
        x: 18,
        y: 58,
        size: 92,
      },
      {
        id: "bananas",
        emoji: "🍌",
        label: "Plátanos",
        x: 50,
        y: 54,
        size: 90,
      },
      {
        id: "flipflops",
        emoji: "🩴",
        label: "Chanclas",
        x: 81,
        y: 57,
        size: 94,
      },
    ],
    statements: [
      {
        id: "bananas-between",
        text: "Los plátanos están entre la sandía y las chanclas.",
        isCorrect: true,
      },
      {
        id: "bananas-left-flipflops",
        text: "Los plátanos están a la izquierda de las chanclas.",
        isCorrect: true,
      },
      {
        id: "watermelon-left",
        text: "La sandía está a la izquierda de los plátanos.",
        isCorrect: true,
      },
      {
        id: "all-fruit",
        text: "Los tres objetos son frutas.",
        isCorrect: false,
      },
      {
        id: "flipflops-left",
        text: "Las chanclas están a la izquierda de la sandía.",
        isCorrect: false,
      },
    ],
  },
  {
    id: "house-birds",
    difficulty: "hard",
    category: "Comprensión espacial compleja",
    background: "field",
    items: [
      {
        id: "tree-left",
        emoji: "🌳",
        label: "Árbol izquierdo",
        x: 18,
        y: 58,
        size: 112,
      },
      {
        id: "house",
        emoji: "🏠",
        label: "Casa",
        x: 50,
        y: 62,
        size: 112,
      },
      {
        id: "tree-right",
        emoji: "🌳",
        label: "Árbol derecho",
        x: 82,
        y: 58,
        size: 112,
      },
      {
        id: "bird-left",
        emoji: "🐦",
        label: "Pájaro izquierdo",
        x: 19,
        y: 25,
        size: 46,
      },
      {
        id: "bird-right",
        emoji: "🐦",
        label: "Pájaro derecho",
        x: 81,
        y: 25,
        size: 46,
      },
    ],
    statements: [
      {
        id: "bird-left-tree",
        text: "Hay un pájaro encima del árbol situado a la izquierda de la casa.",
        isCorrect: true,
      },
      {
        id: "bird-right-tree",
        text: "Hay un pájaro encima del árbol situado a la derecha de la casa.",
        isCorrect: true,
      },
      {
        id: "house-between",
        text: "La casa está entre los dos árboles.",
        isCorrect: true,
      },
      {
        id: "bird-roof",
        text: "Hay un pájaro sobre el tejado de la casa.",
        isCorrect: false,
      },
      {
        id: "one-tree",
        text: "En la escena aparece un solo árbol.",
        isCorrect: false,
      },
      {
        id: "birds-below",
        text: "Los pájaros están debajo de los árboles.",
        isCorrect: false,
      },
    ],
  },
  {
    id: "island",
    difficulty: "hard",
    category: "Descripción global",
    background: "sea",
    items: [
      {
        id: "island",
        emoji: "🏝️",
        label: "Isla",
        x: 50,
        y: 64,
        size: 155,
      },
      {
        id: "palm-left",
        emoji: "🌴",
        label: "Palmera izquierda",
        x: 29,
        y: 43,
        size: 88,
      },
      {
        id: "palm-middle",
        emoji: "🌴",
        label: "Palmera central",
        x: 50,
        y: 39,
        size: 92,
      },
      {
        id: "palm-right",
        emoji: "🌴",
        label: "Palmera derecha",
        x: 71,
        y: 43,
        size: 88,
      },
      {
        id: "bird",
        emoji: "🐦",
        label: "Pájaro",
        x: 50,
        y: 21,
        size: 46,
      },
      {
        id: "sun",
        emoji: "☀️",
        label: "Sol",
        x: 82,
        y: 16,
        size: 55,
      },
    ],
    statements: [
      {
        id: "island-three-palms",
        text: "La isla tiene tres palmeras.",
        isCorrect: true,
      },
      {
        id: "bird-above-middle",
        text: "El pájaro está por encima de la palmera central.",
        isCorrect: true,
      },
      {
        id: "sun-right",
        text: "El sol está en la parte derecha de la escena.",
        isCorrect: true,
      },
      {
        id: "two-palms",
        text: "En la isla hay solamente dos palmeras.",
        isCorrect: false,
      },
      {
        id: "bird-water",
        text: "El pájaro está dentro del agua.",
        isCorrect: false,
      },
      {
        id: "sun-left",
        text: "El sol está a la izquierda de todas las palmeras.",
        isCorrect: false,
      },
    ],
  },
  {
    id: "shopping-bag",
    difficulty: "hard",
    category: "Dentro, fuera, delante y detrás",
    background: "shop",
    items: [
      {
        id: "bag",
        emoji: "🛍️",
        label: "Bolsa",
        x: 49,
        y: 60,
        size: 135,
      },
      {
        id: "bread",
        emoji: "🥖",
        label: "Pan",
        x: 21,
        y: 68,
        size: 78,
      },
      {
        id: "milk",
        emoji: "🥛",
        label: "Leche",
        x: 76,
        y: 45,
        size: 76,
      },
      {
        id: "apple",
        emoji: "🍎",
        label: "Manzana",
        x: 48,
        y: 50,
        size: 52,
      },
      {
        id: "lettuce",
        emoji: "🥬",
        label: "Lechuga",
        x: 52,
        y: 70,
        size: 59,
      },
    ],
    statements: [
      {
        id: "bread-outside",
        text: "El pan está fuera de la bolsa.",
        isCorrect: true,
      },
      {
        id: "apple-inside",
        text: "La manzana está dentro de la bolsa.",
        isCorrect: true,
      },
      {
        id: "lettuce-inside",
        text: "La lechuga está dentro de la bolsa.",
        isCorrect: true,
      },
      {
        id: "milk-right",
        text: "La leche está a la derecha de la bolsa.",
        isCorrect: true,
      },
      {
        id: "bread-inside",
        text: "El pan está dentro de la bolsa.",
        isCorrect: false,
      },
      {
        id: "all-inside",
        text: "Todos los alimentos están dentro de la bolsa.",
        isCorrect: false,
      },
    ],
  },
  {
    id: "animals",
    difficulty: "hard",
    category: "Categorías y posición",
    background: "nature",
    items: [
      {
        id: "crab",
        emoji: "🦀",
        label: "Cangrejo",
        x: 20,
        y: 65,
        size: 82,
      },
      {
        id: "ladybug",
        emoji: "🐞",
        label: "Mariquita",
        x: 50,
        y: 52,
        size: 70,
      },
      {
        id: "butterfly",
        emoji: "🦋",
        label: "Mariposa",
        x: 80,
        y: 30,
        size: 76,
      },
    ],
    statements: [
      {
        id: "ladybug-between",
        text: "La mariquita está entre el cangrejo y la mariposa.",
        isCorrect: true,
      },
      {
        id: "butterfly-right",
        text: "La mariposa está a la derecha del cangrejo.",
        isCorrect: true,
      },
      {
        id: "three-animals",
        text: "En la escena aparecen tres animales.",
        isCorrect: true,
      },
      {
        id: "all-sea",
        text: "Los tres animales viven en el mar.",
        isCorrect: false,
      },
      {
        id: "crab-middle",
        text: "El cangrejo está en el centro de la imagen.",
        isCorrect: false,
      },
      {
        id: "butterfly-cannot-fly",
        text: "La mariposa no puede volar.",
        isCorrect: false,
      },
    ],
  },
];

const GAME_CONFIG = {
  easy: {
    label: "Fácil",
    totalRounds: 6,
    itemCount: 4,
    allowedDifficulties: ["easy"],
  },

  medium: {
    label: "Media",
    totalRounds: 8,
    itemCount: 5,
    allowedDifficulties: ["easy", "medium"],
  },

  hard: {
    label: "Difícil",
    totalRounds: 10,
    itemCount: 6,
    allowedDifficulties: ["medium", "hard"],
  },
};

const shuffleArray = (items) => {
  const shuffled = [...items];

  for (
    let index = shuffled.length - 1;
    index > 0;
    index -= 1
  ) {
    const randomIndex = Math.floor(
      Math.random() * (index + 1)
    );

    [shuffled[index], shuffled[randomIndex]] = [
      shuffled[randomIndex],
      shuffled[index],
    ];
  }

  return shuffled;
};

const createRound = ({
  itemCount,
  allowedDifficulties,
  previousId = null,
}) => {
  const safeItemCount = Math.min(
    Math.max(Number(itemCount) || 4, 3),
    6
  );

  const difficulties =
    Array.isArray(allowedDifficulties) &&
    allowedDifficulties.length > 0
      ? allowedDifficulties
      : ["easy"];

  const filteredScenes = SCENES_BANK.filter(
    (scene) =>
      scene.id !== previousId &&
      difficulties.includes(scene.difficulty)
  );

  const availableScenes =
    filteredScenes.length > 0
      ? filteredScenes
      : SCENES_BANK.filter(
          (scene) => scene.id !== previousId
        );

  const selectedScene =
    availableScenes[
      Math.floor(Math.random() * availableScenes.length)
    ];

  const correctStatements =
    selectedScene.statements.filter(
      (statement) => statement.isCorrect
    );

  const incorrectStatements =
    selectedScene.statements.filter(
      (statement) => !statement.isCorrect
    );

  const minimumCorrect = Math.min(
    correctStatements.length,
    Math.max(1, Math.floor(safeItemCount / 2))
  );

  const selectedCorrect = shuffleArray(
    correctStatements
  ).slice(0, minimumCorrect);

  const remainingSpaces =
    safeItemCount - selectedCorrect.length;

  const selectedIncorrect = shuffleArray(
    incorrectStatements
  ).slice(0, remainingSpaces);

  let selectedStatements = [
    ...selectedCorrect,
    ...selectedIncorrect,
  ];

  if (selectedStatements.length < safeItemCount) {
    const usedIds = new Set(
      selectedStatements.map((statement) => statement.id)
    );

    const remainingStatements =
      selectedScene.statements.filter(
        (statement) => !usedIds.has(statement.id)
      );

    selectedStatements = [
      ...selectedStatements,
      ...shuffleArray(remainingStatements).slice(
        0,
        safeItemCount - selectedStatements.length
      ),
    ];
  }

  return {
    ...selectedScene,
    statements: shuffleArray(selectedStatements),
  };
};

function SceneCanvas({ scene }) {
  if (!scene) {
    return null;
  }

  return (
    <div
      className={`sentence-image-scene sentence-image-scene-${scene.background}`}
      role="img"
      aria-label={`Escena de ${scene.category}`}
    >
      <div className="sentence-image-scene-ground" />

      {scene.items.map((item) => (
        <div
          key={item.id}
          className="sentence-image-object"
          style={{
            left: `${item.x}%`,
            top: `${item.y}%`,
            fontSize: `${item.size}px`,
          }}
          aria-label={item.label}
          title={item.label}
        >
          <span className="sentence-image-emoji">
            {item.emoji}
          </span>

          {item.badge && (
            <span
              className="sentence-image-object-badge"
              style={{
                backgroundColor: item.badgeColor,
              }}
            >
              {item.badge}
            </span>
          )}
        </div>
      ))}
    </div>
  );
}

function SentenceImageMatch() {
  const [selectedDifficulty, setSelectedDifficulty] =
    useState(null);

  const [config, setConfig] = useState(null);

  const [roundIndex, setRoundIndex] = useState(0);
  const [round, setRound] = useState(null);

  const [started, setStarted] = useState(false);
  const [finished, setFinished] = useState(false);

  const [
    selectedStatementIds,
    setSelectedStatementIds,
  ] = useState([]);

  const [correctSelections, setCorrectSelections] =
    useState(0);

  const [wrongSelections, setWrongSelections] =
    useState(0);

  const [omissions, setOmissions] = useState(0);

  const [perfectRounds, setPerfectRounds] =
    useState(0);

  const [responses, setResponses] = useState([]);

  const [startTime, setStartTime] = useState(null);
  const [endTime, setEndTime] = useState(null);

  const [roundStartTime, setRoundStartTime] =
    useState(null);

  const [responseLocked, setResponseLocked] =
    useState(false);

  const totalRounds = config?.totalRounds || 0;

  const progress = useMemo(
    () => calculateProgress(roundIndex, totalRounds),
    [roundIndex, totalRounds]
  );

  const accuracy = calculateSelectionAccuracy({
    correctSelections,
    wrongSelections,
    omissions,
  });

  const totalErrors = calculateErrorCount(
    wrongSelections,
    omissions
  );

  const averageReactionTime =
    calculateAverageReactionTime(responses);

  const totalTime = calculateTotalTime(
    startTime,
    endTime
  );

  const interpretation = finished
    ? getBasicInterpretation({
        accuracy,
        errors: totalErrors,
        averageReactionTime,
        difficulty: selectedDifficulty,
      })
    : null;

  const startGame = (
    difficultyOrConfig =
      selectedDifficulty || "easy"
  ) => {
    const {
      difficultyKey,
      config: resolvedConfig,
    } = resolveGameConfig(
      difficultyOrConfig,
      GAME_CONFIG
    );

    const defaultConfig =
      GAME_CONFIG[difficultyKey] ||
      GAME_CONFIG.easy;

    const nextConfig = {
      ...defaultConfig,
      ...resolvedConfig,
      difficultyKey,

      totalRounds:
        Number(resolvedConfig.totalRounds) ||
        defaultConfig.totalRounds,

      itemCount: Math.min(
        Math.max(
          Number(resolvedConfig.itemCount) ||
            defaultConfig.itemCount,
          3
        ),
        6
      ),

      allowedDifficulties:
        resolvedConfig.allowedDifficulties ||
        defaultConfig.allowedDifficulties,
    };

    const firstRound = createRound(nextConfig);
    const now = Date.now();

    setSelectedDifficulty(difficultyKey);
    setConfig(nextConfig);

    setRoundIndex(0);
    setRound(firstRound);

    setStarted(true);
    setFinished(false);

    setSelectedStatementIds([]);

    setCorrectSelections(0);
    setWrongSelections(0);
    setOmissions(0);
    setPerfectRounds(0);

    setResponses([]);

    setStartTime(now);
    setEndTime(null);
    setRoundStartTime(now);

    setResponseLocked(false);
  };

  const toggleStatement = (statementId) => {
    if (responseLocked) {
      return;
    }

    setSelectedStatementIds((current) =>
      current.includes(statementId)
        ? current.filter(
            (selectedId) =>
              selectedId !== statementId
          )
        : [...current, statementId]
    );
  };

  const finishGame = () => {
    setEndTime(Date.now());
    setStarted(false);
    setFinished(true);
  };

  const moveToNextRound = () => {
    const nextRoundIndex = roundIndex + 1;

    if (nextRoundIndex >= totalRounds) {
      finishGame();
      return;
    }

    const nextRound = createRound({
      ...config,
      previousId: round.id,
    });

    setRoundIndex(nextRoundIndex);
    setRound(nextRound);

    setSelectedStatementIds([]);
    setResponseLocked(false);
    setRoundStartTime(Date.now());
  };

  const confirmSelection = () => {
    if (
      !round ||
      responseLocked ||
      selectedStatementIds.length === 0
    ) {
      return;
    }

    setResponseLocked(true);

    const responseTime =
      Date.now() - roundStartTime;

    const selectedStatements =
      round.statements.filter((statement) =>
        selectedStatementIds.includes(
          statement.id
        )
      );

    const roundCorrectSelections =
      selectedStatements.filter(
        (statement) => statement.isCorrect
      ).length;

    const roundWrongSelections =
      selectedStatements.filter(
        (statement) => !statement.isCorrect
      ).length;

    const roundOmissions =
      round.statements.filter(
        (statement) =>
          statement.isCorrect &&
          !selectedStatementIds.includes(
            statement.id
          )
      ).length;

    const isPerfectRound =
      roundWrongSelections === 0 &&
      roundOmissions === 0;

    setCorrectSelections(
      (current) =>
        current + roundCorrectSelections
    );

    setWrongSelections(
      (current) =>
        current + roundWrongSelections
    );

    setOmissions(
      (current) => current + roundOmissions
    );

    if (isPerfectRound) {
      setPerfectRounds(
        (current) => current + 1
      );
    }

    setResponses((current) => [
      ...current,
      responseTime,
    ]);

    window.setTimeout(() => {
      moveToNextRound();
    }, 1200);
  };

  const getStatementClassName = (statement) => {
    const isSelected =
      selectedStatementIds.includes(statement.id);

    const classNames = [
      "sentence-image-statement",
    ];

    if (isSelected) {
      classNames.push(
        "sentence-image-statement-selected"
      );
    }

    if (responseLocked) {
      if (statement.isCorrect) {
        classNames.push(
          "sentence-image-statement-correct"
        );
      } else if (isSelected) {
        classNames.push(
          "sentence-image-statement-error"
        );
      }
    }

    return classNames.join(" ");
  };

  if (!started && !finished) {
    return (
      <GameLauncher
        title="Lectura de frases"
        description="Observa la escena y selecciona todas las frases que describen correctamente lo que aparece."
        areas={[
          "Comprensión lectora",
          "Atención visual",
          "Conceptos espaciales",
          "Vocabulario",
        ]}
        defaultDifficulty="easy"
        customFields={[
          GameFields.totalRounds({
            min: 4,
            max: 20,
            defaultValue: 8,
          }),

          GameFields.itemCount({
            label: "Frases por escena",
            min: 3,
            max: 6,
            defaultValue: 4,
          }),
        ]}
        onStart={startGame}
      />
    );
  }

  if (finished) {
    return (
      <div className="sentence-image-page">
        <div className="sentence-image-results">
          <GameResults
            title="Resultados"
            correctAnswers={perfectRounds}
            totalAnswers={totalRounds}
            accuracy={accuracy}
            errors={totalErrors}
            omissions={omissions}
            averageReactionTime={
              averageReactionTime
            }
            totalTime={totalTime}
            onRestart={() =>
              startGame(
                selectedDifficulty || "easy"
              )
            }
          />

          <div className="sentence-image-results-grid">
            <div className="sentence-image-result-card">
              <span>Rondas perfectas</span>
              <strong>
                {perfectRounds}/{totalRounds}
              </strong>
            </div>

            <div className="sentence-image-result-card">
              <span>Frases verdaderas seleccionadas</span>
              <strong>{correctSelections}</strong>
            </div>

            <div className="sentence-image-result-card">
              <span>Frases incorrectas seleccionadas</span>
              <strong>{wrongSelections}</strong>
            </div>

            <div className="sentence-image-result-card">
              <span>Frases verdaderas omitidas</span>
              <strong>{omissions}</strong>
            </div>
          </div>

          <GameInterpretation
            interpretation={interpretation}
          />

          <button
            type="button"
            className="sentence-image-restart"
            onClick={() =>
              startGame(
                selectedDifficulty || "easy"
              )
            }
          >
            Jugar de nuevo
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="sentence-image-page">
      <main className="sentence-image-card">
        <header className="sentence-image-header">
          <div>
            <p className="sentence-image-area">
              Comprensión y lectura
            </p>

            <h1>Lectura de frases</h1>

            <p>
              Observa la escena y selecciona todas
              las frases verdaderas.
            </p>
          </div>

          <div className="sentence-image-score">
            <span>Rondas perfectas</span>
            <strong>{perfectRounds}</strong>
          </div>
        </header>

        <GameProgress
          currentRound={roundIndex + 1}
          totalRounds={totalRounds}
          progress={progress}
        />

        <section className="sentence-image-game">
          <span className="sentence-image-category">
            {round?.category}
          </span>

          <SceneCanvas scene={round} />

          <div className="sentence-image-instruction">
            <strong>
              ¿Qué frases describen correctamente
              la escena?
            </strong>

            <span>
              Puede haber más de una respuesta
              correcta.
            </span>
          </div>

          <div className="sentence-image-statements">
            {round?.statements.map(
              (statement, index) => {
                const isSelected =
                  selectedStatementIds.includes(
                    statement.id
                  );

                return (
                  <button
                    type="button"
                    key={statement.id}
                    className={getStatementClassName(
                      statement
                    )}
                    onClick={() =>
                      toggleStatement(
                        statement.id
                      )
                    }
                    disabled={responseLocked}
                    aria-pressed={isSelected}
                  >
                    <span className="sentence-image-statement-number">
                      {index + 1}
                    </span>

                    <span className="sentence-image-statement-text">
                      {statement.text}
                    </span>

                    <span
                      className="sentence-image-statement-check"
                      aria-hidden="true"
                    >
                      {isSelected ? "✓" : ""}
                    </span>
                  </button>
                );
              }
            )}
          </div>

          <div className="sentence-image-actions">
            <div className="sentence-image-selection-count">
              <span>Seleccionadas</span>
              <strong>
                {selectedStatementIds.length}
              </strong>
            </div>

            <button
              type="button"
              className="sentence-image-confirm"
              onClick={confirmSelection}
              disabled={
                selectedStatementIds.length ===
                  0 || responseLocked
              }
            >
              {responseLocked
                ? "Comprobando..."
                : "Confirmar respuestas"}
            </button>
          </div>
        </section>
      </main>
    </div>
  );
}

export default SentenceImageMatch;